/**
 * Pure helpers for rendering the unified network version list.
 *
 * The backend returns one flat array of ``SnapshotEntry`` objects, each
 * tagged with ``kind=BASELINE|RELEASE|CHECKPOINT``. The UI's job is to
 * surface that list in a way that keeps the three kinds visually
 * distinguishable without fracturing them across separate tabs. These
 * helpers produce:
 *
 *   * Stable display labels per entry ("Baseline", "v3", "Checkpoint before
 *     restoring v3").
 *   * A normalized "capturedAt" the UI can sort against regardless of
 *     which backend field holds the timestamp.
 *   * A boolean ``isRestorable`` flag so disabled entries (e.g. the
 *     synthetic "current state" pseudo-entry, if we ever add one) don't
 *     trigger the restore dialog.
 *
 * Everything here must stay pure: no React, no fetch, no ``Date.now()``
 * calls that would make Vitest flaky.
 */

import { displayVersionLabel } from './stepflowVersionLabel.js';

export const SNAPSHOT_KIND_BASELINE = 'BASELINE';
export const SNAPSHOT_KIND_RELEASE = 'RELEASE';
export const SNAPSHOT_KIND_CHECKPOINT = 'CHECKPOINT';
// Separate from ``CHECKPOINT`` so the UI row can render its own label /
// actions. Backend-side the two share a row type (``NetworkCheckpoint``);
// only ``reason`` differs. See ``_serialize_network_checkpoint``.
export const SNAPSHOT_KIND_USER = 'USER_SNAPSHOT';

export const RELEASE_STATUS_ACTIVE = 'ACTIVE_RELEASE';
export const RELEASE_STATUS_DEACTIVATED = 'DEACTIVATED';

/**
 * PR 4: graph-label chip. Returns one of:
 *   - ``'graph+config'`` — the snapshot's rich ASL differs from baseline
 *   - ``'config-only'``   — the snapshot matches baseline's ASL; only
 *                           agent/runtime config changed
 *   - ``null``            — the server couldn't determine (either the row
 *                           or the baseline had no rich ASL). UI hides
 *                           the chip.
 *
 * BASELINE rows always return ``null`` — they are the reference, so
 * "matches baseline" is tautological and adds noise to the list.
 */
export function snapshotGraphLabel(entry) {
  if (!entry || typeof entry !== 'object') return null;
  if (entry.kind === SNAPSHOT_KIND_BASELINE) return null;
  if (entry.matchesBaselineGraph === true) return 'config-only';
  if (entry.matchesBaselineGraph === false) return 'graph+config';
  return null;
}

/**
 * Return the canonical ``capturedAt`` string for sorting / display.
 *
 * Release entries use ``releasedAt``; baseline and checkpoint use
 * ``capturedAt``. Downstream code should never read the kind-specific
 * field directly — call this and you'll sort correctly across kinds.
 */
export function snapshotCapturedAt(entry) {
  if (!entry || typeof entry !== 'object') return '';
  if (entry.kind === SNAPSHOT_KIND_RELEASE) {
    return String(entry.releasedAt || '');
  }
  return String(entry.capturedAt || '');
}

/**
 * Sort snapshots newest-first. Pure; returns a new array.
 *
 * Sort key is ``capturedAt`` (ISO-8601 strings). If two entries tie (rare —
 * only happens if two releases and a checkpoint share a timestamp) we fall
 * back to ``kind`` ordering so the list is deterministic: BASELINE comes
 * first, then RELEASE, then CHECKPOINT. That keeps the baseline pinned to
 * the visual "start" of the history even when a same-instant checkpoint
 * gets captured during the first restore.
 */
const KIND_ORDER = {
  [SNAPSHOT_KIND_BASELINE]: 0,
  [SNAPSHOT_KIND_RELEASE]: 1,
  // User snapshots sort above auto-checkpoints on ties: a creator-labeled
  // save is more "important" than a system-generated one.
  [SNAPSHOT_KIND_USER]: 2,
  [SNAPSHOT_KIND_CHECKPOINT]: 3,
};

export function sortSnapshotsNewestFirst(snapshots) {
  const list = Array.isArray(snapshots) ? snapshots.slice() : [];
  list.sort((a, b) => {
    const at = snapshotCapturedAt(a);
    const bt = snapshotCapturedAt(b);
    if (at !== bt) return at < bt ? 1 : -1;
    const ak = KIND_ORDER[a?.kind] ?? 99;
    const bk = KIND_ORDER[b?.kind] ?? 99;
    return ak - bk;
  });
  return list;
}

/**
 * Human-readable primary label for a snapshot row.
 *
 *   BASELINE  → "Golden baseline"  (BRAIN_DEPLOY)
 *             → "Promoted baseline" (DRIFT_PROMOTE — label warns of drift)
 *   RELEASE   → "Released v3"
 *   CHECKPOINT→ "Before restoring v3"
 *             → "Before restoring baseline"
 *             → "Before restoring checkpoint (Apr 20 10:00)"
 *             → "Auto-checkpoint" (fallback if source info missing)
 */
export function snapshotPrimaryLabel(entry, releases) {
  if (!entry || typeof entry !== 'object') return 'Snapshot';
  if (entry.kind === SNAPSHOT_KIND_BASELINE) {
    return entry.source === 'DRIFT_PROMOTE'
      ? 'Promoted baseline'
      : 'Golden baseline';
  }
  if (entry.kind === SNAPSHOT_KIND_RELEASE) {
    const v = Number(entry.releaseVersion);
    const prefix = entry.releaseStatus === RELEASE_STATUS_DEACTIVATED
      ? 'Deactivated'
      : 'Released';
    if (Number.isFinite(v) && v > 0) {
      // Authoritative MAJOR.MINOR label (server ``stepflowVersion``) or the
      // client fallback — never the retired ``vN``. When there's no semantic
      // version, fall through to the generic label below.
      const label = displayVersionLabel(entry, releases);
      if (label) return `${prefix} ${label}`;
    }
    return entry.releaseStatus === RELEASE_STATUS_DEACTIVATED
      ? 'Deactivated release'
      : 'Release';
  }
  if (entry.kind === SNAPSHOT_KIND_USER) {
    const label = (entry.label || '').trim();
    return label ? `Saved — ${label}` : 'Saved snapshot';
  }
  if (entry.kind === SNAPSHOT_KIND_CHECKPOINT) {
    // The reason tells us which auto path wrote this. "pre-run" snapshots
    // come from the Run tab and deserve their own label so creators don't
    // confuse them with pre-restore safeties.
    const reason = String(entry.reason || '').toLowerCase();
    if (reason === 'pre-run') return 'Auto-saved before run';
    const sk = String(entry.sourceKind || '').toUpperCase();
    if (sk === SNAPSHOT_KIND_RELEASE) {
      const v = Number(entry.sourceVersion);
      if (Number.isFinite(v) && v > 0) {
        const lbl = displayVersionLabel(v, releases);
        return lbl ? `Before restoring ${lbl}` : 'Before restoring release';
      }
      return 'Before restoring release';
    }
    if (sk === SNAPSHOT_KIND_BASELINE) return 'Before restoring baseline';
    if (sk === SNAPSHOT_KIND_CHECKPOINT) {
      return 'Before restoring checkpoint';
    }
    return 'Auto-checkpoint';
  }
  return 'Snapshot';
}

/**
 * Supporting sub-label for a snapshot row — typically the timestamp and
 * actor. Kept optional on the UI (the workspace layout may prefer tooltips)
 * but centralized here so the copy is consistent.
 */
export function snapshotSubLabel(entry) {
  if (!entry || typeof entry !== 'object') return '';
  const at = snapshotCapturedAt(entry);
  const by =
    entry.kind === SNAPSHOT_KIND_RELEASE
      ? entry.releasedBy
      : entry.capturedBy;
  if (at && by) return `${at} · ${by}`;
  return at || '';
}

/**
 * Kind-aware badge text for a snapshot row. The UI uses this for the
 * pill-shaped label on the left of each row. Releases also render a
 * secondary lifecycle pill ("Active" / "Deactivated" / "Delete eligible")
 * — see ``releaseLifecycleBadge`` below.
 */
export function snapshotKindBadge(entry) {
  if (!entry || typeof entry !== 'object') return 'Snapshot';
  switch (entry.kind) {
    case SNAPSHOT_KIND_BASELINE:
      return 'Baseline';
    case SNAPSHOT_KIND_RELEASE:
      return 'Release';
    case SNAPSHOT_KIND_USER:
      return 'Saved';
    case SNAPSHOT_KIND_CHECKPOINT:
      return 'Auto';
    default:
      return 'Snapshot';
  }
}

/**
 * Lifecycle badge for release rows. Returns ``null`` for non-release
 * entries so callers can conditionally render.
 *
 *   ACTIVE_RELEASE                → "Active"
 *   DEACTIVATED && !deleteEligible → "Deactivated"
 *   DEACTIVATED && deleteEligible  → "Delete eligible"
 */
export function releaseLifecycleBadge(entry) {
  if (!entry || entry.kind !== SNAPSHOT_KIND_RELEASE) return null;
  const status = entry.releaseStatus || RELEASE_STATUS_ACTIVE;
  if (status === RELEASE_STATUS_ACTIVE) return { label: 'Active', tone: 'success' };
  if (status === RELEASE_STATUS_DEACTIVATED && entry.deleteEligible) {
    return { label: 'Delete eligible', tone: 'danger' };
  }
  if (status === RELEASE_STATUS_DEACTIVATED) {
    return { label: 'Deactivated', tone: 'warning' };
  }
  return null;
}

/**
 * Decide which actions the row-level kebab menu should expose. Kept as a
 * pure function so Vitest can exhaustively enumerate the matrix without
 * rendering any React.
 *
 * Action IDs match the handler-call names used by ``SnapshotsTab``:
 *
 *   makeActive   — restore this snapshot's state into the live network
 *                  (semantic restore, not lifecycle). Available on every
 *                  kind except baseline when there's no changes to apply
 *                  (the tab decides per-row).
 *   rename       — USER_SNAPSHOT only (labels are user-owned).
 *   delete       — USER_SNAPSHOT always, CHECKPOINT always (they're
 *                  ephemeral), RELEASE only when ``deleteEligible``.
 *   deactivate   — RELEASE with status=ACTIVE_RELEASE only.
 *   reactivate   — RELEASE with status=DEACTIVATED AND NOT delete-eligible.
 *
 * Returns an array of ``{ id, label, disabled?, tone? }`` in the order the
 * UI should render them. ``tone`` is used for destructive actions (red).
 */
export function snapshotRowActions(entry, opts = {}) {
  if (!entry || typeof entry !== 'object') return [];
  const actions = [];
  const kind = entry.kind;
  // Proven-network gate (per-network, simple). "Release to registry" is enabled once
  // the network has ≥1 successful run (from GET /networks/{id} → hasSuccessfulRun);
  // shown DISABLED + "(not tested)" until then. (A per-VERSION gate was reverted — see
  // NETWORK-REGISTRY.md §13 TODO.)
  const hasSuccessfulRun = opts.hasSuccessfulRun !== false;

  // "Make active" — applies to everything except the baseline rendering
  // when we already view the network AS the baseline (v1 can skip the
  // refinement — we always surface Make Active for baseline too since it
  // is the canonical rollback target).
  if (
    kind === SNAPSHOT_KIND_BASELINE
    || kind === SNAPSHOT_KIND_RELEASE
    || kind === SNAPSHOT_KIND_USER
    || kind === SNAPSHOT_KIND_CHECKPOINT
  ) {
    // Deactivated releases CAN still be the restore source: their snapshot
    // is immutable and restoring from them doesn't re-activate the
    // release. The UI still renders it greyed with a tooltip if the
    // creator has chosen to treat deactivated as "off-limits"; that's a
    // future refinement. For v1 we allow it so creators aren't boxed in.
    actions.push({ id: 'makeActive', label: 'Make active' });
  }

  if (kind === SNAPSHOT_KIND_USER) {
    actions.push({ id: 'rename', label: 'Rename' });
    actions.push({ id: 'delete', label: 'Delete', tone: 'danger' });
  }

  if (kind === SNAPSHOT_KIND_CHECKPOINT) {
    actions.push({ id: 'delete', label: 'Delete', tone: 'danger' });
  }

  if (kind === SNAPSHOT_KIND_RELEASE) {
    const status = entry.releaseStatus || RELEASE_STATUS_ACTIVE;
    if (status === RELEASE_STATUS_ACTIVE) {
      actions.push({ id: 'deactivate', label: 'Deactivate', tone: 'warning' });
    }
    if (status === RELEASE_STATUS_DEACTIVATED && !entry.deleteEligible) {
      actions.push({ id: 'reactivate', label: 'Reactivate' });
    }
    if (status === RELEASE_STATUS_DEACTIVATED && entry.deleteEligible) {
      actions.push({ id: 'delete', label: 'Delete', tone: 'danger' });
    }
    // Publish this proven release as a package to the shared-services network
    // registry (NETWORK-REGISTRY.md). Gated on a successful run: shown always, but
    // DISABLED + "(not tested)" until the network has run once to success — so the
    // user sees WHY it's unavailable rather than the option silently missing.
    actions.push(
      hasSuccessfulRun
        ? { id: 'publish', label: 'Release to registry' }
        : { id: 'publish', label: 'Release to registry (not tested)', disabled: true,
            title: 'Run this network in a room to success at least once before publishing.' },
    );
  }

  // PR 4: Export — only when the list payload says a claim-check manifest
  // exists (``manifestReady``). Older APIs omit the field; treat that as
  // unknown and still show Export so we do not hide a working path during
  // rollout. ``manifestReady === false`` means DDB row has no S3Key —
  // export would 404, so omit the action.
  if (toRestoreSource(entry) && entry.manifestReady !== false) {
    actions.push({ id: 'export', label: 'Export manifest' });
  }

  return actions;
}

/**
 * Turn an entry into the ``RestoreSource`` object the API helpers expect.
 * Returns ``null`` when the entry isn't restorable (e.g. unknown kind).
 */
export function toRestoreSource(entry) {
  if (!entry || typeof entry !== 'object') return null;
  if (entry.kind === SNAPSHOT_KIND_BASELINE) {
    return { sourceKind: SNAPSHOT_KIND_BASELINE };
  }
  if (entry.kind === SNAPSHOT_KIND_RELEASE) {
    const v = Number(entry.releaseVersion);
    if (!Number.isFinite(v) || v < 1) return null;
    return { sourceKind: SNAPSHOT_KIND_RELEASE, sourceVersion: Math.floor(v) };
  }
  // User snapshots and auto checkpoints share a storage row and both
  // resolve to ``sourceKind=CHECKPOINT`` for the restore API. The
  // ``sourceCheckpointAt`` pointer disambiguates.
  if (
    entry.kind === SNAPSHOT_KIND_CHECKPOINT
    || entry.kind === SNAPSHOT_KIND_USER
  ) {
    const at = String(entry.capturedAt || '').trim();
    if (!at) return null;
    return {
      sourceKind: SNAPSHOT_KIND_CHECKPOINT,
      sourceCheckpointAt: at,
    };
  }
  return null;
}

/**
 * Stable DOM key for an entry — composites ``kind`` with the entry's own
 * identifier (release version / captured-at) so React doesn't reuse nodes
 * when the user refreshes.
 */
export function snapshotDomKey(entry) {
  if (!entry || typeof entry !== 'object') return 'unknown';
  if (entry.kind === SNAPSHOT_KIND_BASELINE) return 'baseline';
  if (entry.kind === SNAPSHOT_KIND_RELEASE) {
    return `release:${entry.releaseVersion ?? '?'}`;
  }
  if (entry.kind === SNAPSHOT_KIND_USER) {
    return `user:${entry.capturedAt ?? '?'}`;
  }
  if (entry.kind === SNAPSHOT_KIND_CHECKPOINT) {
    return `checkpoint:${entry.capturedAt ?? '?'}`;
  }
  return 'unknown';
}

/**
 * Locate the newest checkpoint in a snapshot list — powers the
 * post-restore undo toast. Returns ``null`` when no checkpoints exist.
 */
export function newestCheckpoint(snapshots) {
  if (!Array.isArray(snapshots)) return null;
  const sorted = sortSnapshotsNewestFirst(
    snapshots.filter((s) => s && s.kind === SNAPSHOT_KIND_CHECKPOINT),
  );
  return sorted[0] || null;
}
